import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import Popup from "../../Popup/Popup";
import Button from "../../Button/Button";
import { ImproveAreaDisplayInfo } from "../../../../Types/AreaDisplayInfo/AreaDisplayInfo";
import { ImproveTemplateDto } from "../../../../Types/Improve/ImproveTemplateDto";
import { downloadBlob } from "../../../../Utils/Export/downloadBlob";
import { openBlobInNewTab } from "../../../../Utils/Export/openInNewTab";
import { fetchImproveExportData } from "../../../../Utils/Export/ImproveExport/fetchImproveExportData";
import { improveDocxExporter } from "../../../../Utils/Export/ImproveExport/improveDocxExporter";
import { improveHtmlExporter } from "../../../../Utils/Export/ImproveExport/improveHtmlExporter";
import { improveXlsxExporter } from "../../../../Utils/Export/ImproveExport/improveXlsxExporter";
import {
  DEFAULT_IMPROVE_EXPORT_FIELD_VISIBILITY,
  IMPROVE_EXPORT_COLS,
  ImproveExportColumnKey,
  ImproveExportFieldVisibility,
  fullImproveExportFieldVisibility,
} from "../../../../Utils/Export/ImproveExport/types";
import {
  buildInitialImproveExportFieldVisibility,
  getImproveExportColumnToggleableMap,
  isImproveExportColumnToggleable,
} from "../../../../Utils/Export/ImproveExport/improveExportTemplateFields";
import type { ExportFormat } from "../../../../Utils/Export/types";
import ExportFormatChoiceGrid from "../../ExportFormatChoice/ExportFormatChoiceGrid";
import {
  EXPORT_FORMAT_ORDER,
  getExportFormatUnavailableReason,
} from "../../ExportFormatChoice/exportFormatDisplay";
import ImproveExportColumnPicker from "../../ExportButton/ImproveExportColumnPicker";
import { EXPORT_POPUP_SHELL_STYLE } from "../../../../Utils/Export/exportPopupShellStyle";
import styles from "../BulkExportWizard/BulkExportWizard.module.css";

function defaultBaseFilename(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `Mesma-Track-Export-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}`;
}

export type ImproveExportWizardProps = {
  isVisible: boolean;
  onClose: () => void;
  areas: ImproveAreaDisplayInfo[];
  /** When true, export payloads use archived improvement rows (archive tab). */
  archivedPlans?: boolean;
  /** Pre-select these plan ids when the wizard opens (e.g. per-card export). */
  initialSelectedIds?: string[];
};

const ImproveExportWizard: React.FC<ImproveExportWizardProps> = ({
  isVisible,
  onClose,
  areas,
  archivedPlans = false,
  initialSelectedIds,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [fieldVisibility, setFieldVisibility] =
    useState<ImproveExportFieldVisibility>(() => ({
      ...DEFAULT_IMPROVE_EXPORT_FIELD_VISIBILITY,
    }));
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<ImproveTemplateDto[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const columnInitKeyRef = useRef("");

  const exportable = useMemo(
    () =>
      areas
        .filter((a) => a.canExport)
        .slice()
        .sort((a, b) =>
          (a.title || "Untitled").localeCompare(b.title || "Untitled", undefined, {
            sensitivity: "base",
          })
        ),
    [areas]
  );

  const allExportableSelected = useMemo(
    () =>
      exportable.length > 0 &&
      exportable.every((a) => selectedIds.has(a.id)),
    [exportable, selectedIds]
  );

  const toggleSelectAllExportable = useCallback(() => {
    if (exportable.length === 0) return;
    if (allExportableSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(exportable.map((a) => a.id)));
    }
  }, [allExportableSelected, exportable]);

  useEffect(() => {
    if (!isVisible) {
      setStep(1);
      setSelectedIds(new Set());
      setFieldVisibility({ ...DEFAULT_IMPROVE_EXPORT_FIELD_VISIBILITY });
      setError(null);
      setRunning(false);
      setTemplates([]);
      setTemplatesLoading(false);
      columnInitKeyRef.current = "";
      return;
    }

    if (initialSelectedIds?.length) {
      const exportableIds = new Set(exportable.map((a) => a.id));
      const preselected = initialSelectedIds.filter((id) => exportableIds.has(id));
      setSelectedIds(new Set(preselected));
    } else {
      setSelectedIds(new Set());
    }
  }, [isVisible, initialSelectedIds, exportable]);

  const toggleArea = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setColumnOn = useCallback(
    (key: ImproveExportColumnKey, on: boolean) => {
      if (!isImproveExportColumnToggleable(key, templates)) return;
      setFieldVisibility((prev) => ({ ...prev, [key]: on }));
    },
    [templates]
  );

  const toggleableMap = useMemo(
    () => getImproveExportColumnToggleableMap(templates),
    [templates]
  );

  useEffect(() => {
    if (!isVisible || step < 2) return;

    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setTemplates([]);
      setTemplatesLoading(false);
      return;
    }

    let cancelled = false;
    setTemplatesLoading(true);
    fetchImproveExportData(ids, { archived: archivedPlans })
      .then((data) => {
        if (!cancelled) {
          setTemplates(data.map((d) => d.template));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTemplates([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTemplatesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [archivedPlans, isVisible, selectedIds, step]);

  useEffect(() => {
    if (step !== 2 || templates.length === 0) return;

    const initKey = `${Array.from(selectedIds).sort().join(",")}:${templates
      .map((t) => t.id)
      .sort()
      .join(",")}`;
    if (columnInitKeyRef.current === initKey) return;

    columnInitKeyRef.current = initKey;
    setFieldVisibility(buildInitialImproveExportFieldVisibility(templates));
  }, [selectedIds, step, templates]);

  const activeColumnCount = useMemo(
    () => IMPROVE_EXPORT_COLS.filter((k) => fieldVisibility[k]).length,
    [fieldVisibility]
  );

  const allColumnsSelected = useMemo(() => {
    const keys = IMPROVE_EXPORT_COLS.filter((k) => toggleableMap[k]);
    return keys.length > 0 && keys.every((k) => fieldVisibility[k]);
  }, [fieldVisibility, toggleableMap]);

  const toggleSelectAllColumns = useCallback(() => {
    setFieldVisibility((prev) => {
      const toggleableKeys = IMPROVE_EXPORT_COLS.filter((k) => toggleableMap[k]);
      const allOn =
        toggleableKeys.length > 0 && toggleableKeys.every((k) => prev[k]);
      const next = { ...prev };
      for (const k of IMPROVE_EXPORT_COLS) {
        if (toggleableMap[k]) next[k] = !allOn;
        else next[k] = false;
      }
      return next;
    });
  }, [toggleableMap]);

  const runExport = useCallback(
    async (format: ExportFormat) => {
      if (getExportFormatUnavailableReason(format, true)) {
        return;
      }

      const ids = Array.from(selectedIds);
      if (ids.length === 0) {
        toast.error("No track plans selected to export.");
        return;
      }
      if (activeColumnCount === 0) {
        setError("Select at least one field to include in the export.");
        return;
      }

      const exporter =
        format === "xlsx"
          ? improveXlsxExporter
          : format === "docx"
            ? improveDocxExporter
            : improveHtmlExporter;
      const ext =
        format === "xlsx" ? "xlsx" : format === "docx" ? "docx" : "html";

      setRunning(true);
      setError(null);
      try {
        const vis = fullImproveExportFieldVisibility(fieldVisibility);
        const data = await fetchImproveExportData(ids, {
          archived: archivedPlans,
        });
        if (data.length === 0) {
          toast.error("No data returned for export. Check your permissions.");
          return;
        }
        const base = defaultBaseFilename();
        const blob = await exporter(data, base, { fieldVisibility: vis });
        if (format === "html") {
          openBlobInNewTab(blob);
        } else {
          downloadBlob(blob, `${base}.${ext}`);
        }
        toast.success(
          ids.length > 1
            ? `Exported ${ids.length} track plans.`
            : "Export completed."
        );
        onClose();
      } catch (e) {
        console.error(e);
        toast.error("Export failed. Please try again.");
      } finally {
        setRunning(false);
      }
    },
    [
      activeColumnCount,
      archivedPlans,
      fieldVisibility,
      onClose,
      selectedIds,
    ]
  );

  const canNextStep1 = selectedIds.size > 0;
  const canNextStep2 = activeColumnCount > 0 && !templatesLoading;

  return (
    <Popup
      isVisible={isVisible}
      onClose={onClose}
      style={EXPORT_POPUP_SHELL_STYLE}
    >
      <div className={styles.content}>
        <h3 className={styles.contentTitle}>Export track plans</h3>

        <div className={styles.stepMain}>
          {step === 1 ? (
            <>
              <p className={styles.stepHint}>
                Select one or more track plans to include in the export.
              </p>
              <div className={styles.listToolbar}>
                <Button
                  size="Small"
                  colour="#FFF"
                  textColour="#000"
                  border="rgba(0,0,0,0.2)"
                  disabled={running || exportable.length === 0}
                  clickHandler={toggleSelectAllExportable}
                >
                  {allExportableSelected ? "Deselect all" : "Select all"}
                </Button>
              </div>
              <div
                className={styles.list}
                role="group"
                aria-label="Track plans"
              >
                {exportable.length === 0 ? (
                  <p className={styles.stepHint}>Nothing available to export.</p>
                ) : (
                  exportable.map((a) => (
                    <label key={a.id} className={styles.areaRow}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(a.id)}
                        onChange={() => toggleArea(a.id)}
                      />
                      <span>{a.title || "Untitled"}</span>
                    </label>
                  ))
                )}
              </div>
            </>
          ) : null}

          {step === 2 ? (
            <ImproveExportColumnPicker
              fieldVisibility={fieldVisibility}
              templates={templates}
              busy={running || templatesLoading}
              onColumnChange={setColumnOn}
              onToggleSelectAll={toggleSelectAllColumns}
              allColumnsSelected={allColumnsSelected}
            />
          ) : null}

          {step === 3 ? (
            <>
              <p className={styles.stepHint}>
                Choose a file format. Your column choices apply to Excel, Word,
                and HTML.
              </p>
              <div className={styles.formatRow}>
                <ExportFormatChoiceGrid
                  variant="tiles"
                  formats={EXPORT_FORMAT_ORDER}
                  trackExport
                  disabled={running}
                  onChoose={(fmt) => void runExport(fmt)}
                />
                {running ? (
                  <p className={styles.formatBusy} aria-live="polite">
                    Working on your export…
                  </p>
                ) : null}
              </div>
            </>
          ) : null}
        </div>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.actions}>
          {step > 1 ? (
            <Button
              size="Small"
              colour="#FFF"
              textColour="#000"
              border="rgba(0,0,0,0.2)"
              disabled={running}
              clickHandler={() =>
                setStep((s) => (s === 3 ? 2 : 1) as 1 | 2 | 3)
              }
            >
              Back
            </Button>
          ) : null}
          {step < 3 ? (
            <Button
              size="Small"
              colour="rgb(36, 57, 79)"
              textColour="#FFF"
              border="transparent"
              disabled={
                running ||
                (step === 1 && !canNextStep1) ||
                (step === 2 && !canNextStep2)
              }
              clickHandler={() => {
                setError(null);
                if (step === 1) {
                  columnInitKeyRef.current = "";
                  setTemplates([]);
                  setTemplatesLoading(false);
                }
                setStep((s) => (s === 1 ? 2 : 3));
              }}
            >
              Next
            </Button>
          ) : null}
          <Button
            size="Small"
            colour="#FFF"
            textColour="#000"
            border="rgba(0,0,0,0.2)"
            disabled={running}
            clickHandler={onClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Popup>
  );
};

export default ImproveExportWizard;
